import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Container } from '../../UI/Containers';
import Table from '../../UI/Table';
import { Button } from '../../UI/Buttons';
import attendanceService from '../../../Services/Api/attendanceService';

const ViewContainer = styled(Container)`
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const Controls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const FlexSpacer = styled.div`
  flex: 1;
`;

const DownloadWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Dropdown = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.small};
  border-radius: 6px;
  padding: ${({ theme }) => theme.spacing.sm};
  min-width: 160px;
  z-index: 40;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const DateInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
`;

function getCycleRange(date = new Date()) {
  const d = new Date(date);
  const day = d.getDate();
  let start, end;

  if (day >= 25) {
    // start = 25th of current month
    start = new Date(d.getFullYear(), d.getMonth(), 25);
    // end = 24th of next month
    end = new Date(d.getFullYear(), d.getMonth() + 1, 24);
  } else {
    // start = 25th of previous month
    start = new Date(d.getFullYear(), d.getMonth() - 1, 25);
    // end = 24th of current month
    end = new Date(d.getFullYear(), d.getMonth(), 24);
  }

  const fmt = (dt) => dt.toISOString().split('T')[0];
  return { start_date: fmt(start), end_date: fmt(end) };
}

function ReportView() {
  const defaultRange = getCycleRange(new Date());
  const [startDate, setStartDate] = useState(defaultRange.start_date);
  const [endDate, setEndDate] = useState(defaultRange.end_date);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'total_working_days', label: 'Working Days', align: 'center' },
    { key: 'days_present', label: 'Days Present', align: 'center' },
    { key: 'days_absent', label: 'Days Absent', align: 'center' },
    { key: 'leave_days', label: 'Leave Days', align: 'center' },
    { key: 'attendance_percentage', label: 'Attendance %', align: 'center' },
    { key: 'late_count', label: 'Late Count', align: 'center' },
    { key: 'overtime_hours', label: 'Overtime (hrs)', align: 'center' }
  ];

  const fetchReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await attendanceService.getAttendanceReport(startDate, endDate);
      const rows = Array.isArray(res.results) ? res.results.map(r => {
        const total = Number(r.total_working_days || 0);
        const present = Number(r.days_present || 0);
        const leave = Number(r.leave_days || 0);
        // absent = working days minus present minus leave
        const absentVal = Math.max(0, total - present - leave);
        const attendancePct = total > 0 ? ((present + leave) / total) * 100 : 0;
        return ({
          id: r.user?.id,
          name: `${r.user?.first_name || ''} ${r.user?.last_name || ''}`.trim(),
          total_working_days: total,
          days_present: present,
          days_absent: absentVal,
          leave_days: leave,
          attendance_percentage: `${attendancePct.toFixed(2)}%`,
          late_count: r.late_count,
          overtime_hours: Number(r.overtime_hours || 0).toFixed(2)
        });
      }) : [];
      setData(rows);
    } catch (err) {
      setError(err.message || 'Failed to fetch report');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSheetJS = () => new Promise((resolve, reject) => {
    if (window.XLSX) return resolve(window.XLSX);
    const s = document.createElement('script');
    s.src = 'https://unpkg.com/xlsx/dist/xlsx.full.min.js';
    s.onload = () => resolve(window.XLSX);
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });

  const downloadXlsx = async () => {
    try {
      const XLSX = await loadSheetJS();
      // prepare rows from current data
      const rows = data.map(r => ({
        Name: r.name,
        "Working Days": r.total_working_days,
        "Days Present": r.days_present,
        "Days Absent": r.days_absent,
        "Leave Days": r.leave_days,
        "Attendance %": r.attendance_percentage,
        "Late Count": r.late_count,
        "Overtime (hrs)": r.overtime_hours
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // style header (best-effort)
      const headerRange = XLSX.utils.decode_range(ws['!ref']);
      for (let C = headerRange.s.c; C <= headerRange.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: 'FFFFFFFF' } },
          fill: { fgColor: { rgb: 'FF1976D2' } }
        };
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Report');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance-report-${startDate}-to-${endDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloadOpen(false);
    } catch (e) {
      console.error('Failed to generate XLSX:', e);
    }
  };

  const loadJsPDF = () => new Promise((resolve, reject) => {
    if (window.jspdf && window.jspdf.jsPDF) return resolve(window.jspdf);
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
    s.onload = () => {
      // load autotable plugin next
      const t = document.createElement('script');
      t.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.25/jspdf.plugin.autotable.min.js';
      t.onload = () => resolve(window.jspdf);
      t.onerror = (e) => reject(e);
      document.head.appendChild(t);
    };
    s.onerror = (e) => reject(e);
    document.head.appendChild(s);
  });

  const downloadPdf = async () => {
    try {
      const jspdf = await loadJsPDF();
      const { jsPDF } = jspdf;
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const title = `Attendance Report ${startDate} to ${endDate}`;
      doc.setFontSize(14);
      doc.text(title, 40, 40);

      const headers = [
        'Name', 'Working Days', 'Days Present', 'Days Absent', 'Leave Days', 'Attendance %', 'Late Count', 'Overtime (hrs)'
      ];
      const body = data.map(r => [
        r.name,
        r.total_working_days,
        r.days_present,
        r.days_absent,
        r.leave_days,
        r.attendance_percentage,
        r.late_count,
        r.overtime_hours
      ]);

      // @ts-ignore autotable attaches to jsPDF
      doc.autoTable({ head: [headers], body, startY: 60, styles: { fontSize: 10 } });
      doc.save(`attendance-report-${startDate}-to-${endDate}.pdf`);
      setDownloadOpen(false);
    } catch (e) {
      console.error('Failed to generate PDF:', e);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ViewContainer>
      <Controls>
        <label>
          From:&nbsp;
          <DateInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>

        <label>
          To:&nbsp;
          <DateInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>

        <Button onClick={fetchReport} variant="primary">Fetch</Button>

        <FlexSpacer />

        <DownloadWrapper>
          <Button onClick={() => setDownloadOpen(v => !v)} variant="outline">Download ▾</Button>
          {downloadOpen && (
            <Dropdown>
              <Button variant="ghost" onClick={downloadXlsx}>Download .xlsx</Button>
              <Button variant="ghost" onClick={downloadPdf}>Download PDF</Button>
            </Dropdown>
          )}
        </DownloadWrapper>
      </Controls>

      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        error={error}
      />
    </ViewContainer>
  );
}

export default ReportView;
